import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { FiRefreshCw, FiSearch } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import ErrorBoundary from '../components/ErrorBoundary';

const SECTION_PATH_RE =
    /^\/(god|gz|simon|superman|ads|assets|unblock|sbf|sfrt|redirect)\/audit-log\/?$/i;

function resolveSection(sectionParam, pathname) {
    if (pathname.startsWith('/main-dashboard')) return '';
    if (sectionParam) return String(sectionParam).toLowerCase();
    const match = pathname.match(SECTION_PATH_RE);
    return match ? match[1].toLowerCase() : '';
}

const ACTION_OPTIONS = [
    { value: '', label: 'All actions' },
    { value: 'login', label: 'Login' },
    { value: 'login_denied', label: 'Login denied' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
];

const PROJECT_OPTIONS = [
    { value: '', label: 'All projects' },
    { value: 'god', label: 'GOD' },
    { value: 'watch', label: 'Watch' },
    { value: 'fo2', label: 'FO' },
    { value: 'main', label: 'Main' },
    { value: 'simon', label: 'SIMON' },
    { value: 'superman', label: 'SUPERMAN' },
    { value: 'gz', label: 'GZ' },
    { value: 'unblock', label: 'UNBLOCK' },
    { value: 'redirect', label: 'Redirect' },
    { value: 'sbf', label: 'SBF' },
    { value: 'ads', label: 'ADS' },
    { value: 'assets', label: 'ASSETS' },
    { value: 'sfrt', label: 'SFRT' },
];

const GOD_ENTITY_OPTIONS = [
    { value: '', label: 'All entities' },
    { value: 'session', label: 'Session' },
    { value: 'godMember', label: 'God Member' },
    { value: 'godMemberMedia', label: 'God Member media' },
    { value: 'slgReview', label: 'Review' },
];

const ALL_ENTITY_OPTIONS = [
    ...GOD_ENTITY_OPTIONS,
    { value: 'watchProduct', label: 'Watch product' },
    { value: 'watchProductMedia', label: 'Watch product media' },
    { value: 'watchBlogPost', label: 'Watch blog' },
    { value: 'watchBlogMedia', label: 'Watch blog media' },
    { value: 'watchSelfRegisterCustomer', label: 'Watch customer' },
    { value: 'watchOrder', label: 'Watch order' },
    { value: 'watchConfig', label: 'Watch config' },
];

const ACTION_LABELS = {
    login: 'Login',
    login_denied: 'Login denied',
    create: 'Create',
    update: 'Update',
    delete: 'Delete',
};

const PROJECT_LABELS = {
    god: 'GOD',
    sbf: 'SBF',
    unblock: 'UNBLOCK',
    gz: 'GZ',
    simon: 'SIMON',
    sfrt: 'SFRT',
    ads: 'ADS',
    assets: 'ASSETS',
    superman: 'SUPERMAN',
    watch: 'Watch',
    'watch-admin': 'Watch',
    fo2: 'FO',
    main: 'Main',
    erp: 'Main',
};

const ENTITY_LABELS = {
    session: 'Session',
    godMember: 'God Member',
    godMemberMedia: 'Media',
    slgReview: 'Review',
    watchProduct: 'Product',
    watchProductMedia: 'Product media',
    watchBlogPost: 'Blog',
    watchBlogMedia: 'Blog media',
    watchSelfRegisterCustomer: 'Customer',
    watchOrder: 'Order',
    watchConfig: 'Config',
};

const ACTION_BADGE = {
    login: 'bg-green-100 text-green-800',
    login_denied: 'bg-red-100 text-red-800',
    create: 'bg-blue-100 text-blue-800',
    update: 'bg-yellow-100 text-yellow-800',
    delete: 'bg-gray-100 text-gray-800',
};

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-SG', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const actorLabel = (row) => {
    const name = row.actorName || '';
    const user = row.actorUsername ? `@${row.actorUsername}` : '';
    const tg = row.actorTgId || '';
    return [name, user, tg].filter(Boolean).join(' ') || '—';
};

const ErpAuditLogPage = () => {
    const { section: sectionParam } = useParams();
    const location = useLocation();
    const section = resolveSection(sectionParam, location.pathname);
    const isAllProjects = !section;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [action, setAction] = useState('');
    const [project, setProject] = useState('');
    const [entity, setEntity] = useState('');
    const [openId, setOpenId] = useState('');
    const [sectionLabel, setSectionLabel] = useState('');

    const entityOptions = section === 'god' || !section ? ALL_ENTITY_OPTIONS : GOD_ENTITY_OPTIONS;
    const showProjectFilter = isAllProjects;

    useEffect(() => {
        setCurrentPage(1);
        setEntity('');
        setProject('');
        setSearchInput('');
        setSearchTerm('');
        setOpenId('');
    }, [section]);

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams({
                page: String(currentPage),
                limit: '30',
            });
            if (section) params.set('section', section);
            if (searchTerm) params.set('search', searchTerm);
            if (action) params.set('action', action);
            if (isAllProjects && project) params.set('project', project);
            if (entity) params.set('entity', entity);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/erp-audit-logs?${params.toString()}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
            setTotalPages(res.data.totalPages || 1);
            setSectionLabel(res.data.sectionLabel || '');
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load audit logs');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, action, project, entity, section, isAllProjects]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchTerm(searchInput.trim());
    };

    const title = isAllProjects
        ? 'Audit Log (All Projects)'
        : `${sectionLabel || section.toUpperCase()} Audit Log`;

    return (
        <Layout>
            <ErrorBoundary
                fallbackTitle="Audit Log Error"
                fallbackDescription="Could not load the audit log page."
            >
                <div className="space-y-6 w-full max-w-none px-8 py-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                                <p className="text-gray-600 mt-1 text-sm">
                                    {isAllProjects
                                        ? 'Login and changes across every project. Open a project’s Audit Log to see only that project. Rows older than 90 days are removed; members, products, and files are not changed.'
                                        : `Who logged in and what changed in ${sectionLabel || section}. Rows older than 90 days are removed; the original records are not changed.`}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={fetchLogs}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
                            >
                                <FiRefreshCw className="w-4 h-4" />
                                Refresh
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-4">
                        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        placeholder="Username, name, TGID, summary…"
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            <div className="w-40">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
                                <select
                                    value={action}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setAction(e.target.value);
                                    }}
                                    className="w-full py-2 px-3 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {ACTION_OPTIONS.map((opt) => (
                                        <option key={opt.value || 'all-action'} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {showProjectFilter && (
                                <div className="w-40">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
                                    <select
                                        value={project}
                                        onChange={(e) => {
                                            setCurrentPage(1);
                                            setProject(e.target.value);
                                        }}
                                        className="w-full py-2 px-3 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {PROJECT_OPTIONS.map((opt) => (
                                            <option key={opt.value || 'all-project'} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="w-48">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Entity</label>
                                <select
                                    value={entity}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setEntity(e.target.value);
                                    }}
                                    className="w-full py-2 px-3 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {(section === 'god' ? GOD_ENTITY_OPTIONS : entityOptions).map((opt) => (
                                        <option key={opt.value || 'all-entity'} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                            >
                                Search
                            </button>
                        </form>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                        {loading ? (
                            <div className="text-center py-10 text-gray-500">Loading…</div>
                        ) : items.length === 0 ? (
                            <div className="text-center py-10 text-gray-500">No audit events yet.</div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Time
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Who
                                        </th>
                                        {isAllProjects && (
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Project
                                            </th>
                                        )}
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Action
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Entity
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Summary
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {items.map((row) => {
                                        const id = String(row._id);
                                        const open = openId === id;
                                        const colSpan = isAllProjects ? 6 : 5;
                                        return (
                                            <React.Fragment key={id}>
                                                <tr
                                                    className="hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => setOpenId(open ? '' : id)}
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                        {formatDate(row.at)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">
                                                        {actorLabel(row)}
                                                    </td>
                                                    {isAllProjects && (
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                            {PROJECT_LABELS[row.project] || row.project || '—'}
                                                        </td>
                                                    )}
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span
                                                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                ACTION_BADGE[row.action] || 'bg-gray-100 text-gray-800'
                                                            }`}
                                                        >
                                                            {ACTION_LABELS[row.action] || row.action}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                        {ENTITY_LABELS[row.entity] || row.entity}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">
                                                        {row.summary || row.reason || '—'}
                                                    </td>
                                                </tr>
                                                {open && (
                                                    <tr className="bg-gray-50">
                                                        <td colSpan={colSpan} className="px-6 py-3 text-xs text-gray-600">
                                                            {row.reason ? (
                                                                <div className="mb-1">
                                                                    Reason: <span className="font-medium">{row.reason}</span>
                                                                </div>
                                                            ) : null}
                                                            {row.entityId ? (
                                                                <div className="mb-1">
                                                                    Entity ID: <span className="font-mono">{row.entityId}</span>
                                                                </div>
                                                            ) : null}
                                                            {row.diff ? (
                                                                <pre className="mt-2 whitespace-pre-wrap break-all font-mono text-[11px] bg-white border border-gray-200 rounded p-2 max-h-64 overflow-auto">
                                                                    {JSON.stringify(row.diff, null, 2)}
                                                                </pre>
                                                            ) : (
                                                                <div className="text-gray-400">No field diff stored.</div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {!loading && total > 0 && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                {total} event{total === 1 ? '' : 's'}
                            </p>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </ErrorBoundary>
        </Layout>
    );
};

export default ErpAuditLogPage;
